import { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import _ from "lodash";

import { useBoards } from "../../../../../reactQuery/boards";
import { filterUser } from "../../../../../redux/usersSlice";
import { searchCard } from "../../../../../redux/SearchSlice";
import { useCustomSearchParams } from "../../../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../../../hooks/useResponsive";
import useBoardIdByKey from "../../../../../hooks/useBoardIdByKey";
import { getResponseData } from "../../../../../reactQuery/response";
import { isAdmin } from "../../../../../utils/constants";
import { localStorageInstance } from "../../../../../utils/localStorage";
import { normalizeBoardHeaderData } from "./boardHeaderUtils";

export function useBoardHeaderState() {
  const { id } = useBoardIdByKey();
  const { id: boardKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isMobile = useResponsive("down", "md");
  const showGridView = useSelector((state) => state.search.gridView);
  const { boards } = useBoards();
  const [milestone] = useCustomSearchParams("milestone");
  const [paramsWorklogs, setParamsWorklogs] =
    useCustomSearchParams("worklogs");
  const [selectedUserSearch] = useCustomSearchParams("search");

  const [filterDrawer, setFilterDrawer] = useState(false);
  const [openSearchField, setOpenSearchField] = useState(false);
  const [changeBoardAnchorEl, setChangeBoardAnchorEl] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [attachmentDialog, setAttachmentDialog] = useState({
    right: false,
  });

  const cachedBoard = normalizeBoardHeaderData(
    queryClient.getQueryData(["boardName", id])
  );
  const boardList = useMemo(
    () => getResponseData(boards, []).filter((board) => !board?.is_archive),
    [boards]
  );
  const selectedUsers = useMemo(
    () => selectedUserSearch?.split(","),
    [selectedUserSearch]
  );
  const isPermission = useMemo(
    () =>
      isAdmin() ||
      cachedBoard?.is_owner ||
      cachedBoard?.board_permission?.is_admin,
    [cachedBoard]
  );
  const canAccessBoardHeaderMenu = useMemo(
    () =>
      isAdmin() ||
      cachedBoard?.is_owner ||
      cachedBoard?.board_permission?.is_admin ||
      cachedBoard?.board_permission?.can_create_task,
    [cachedBoard]
  );

  const debouncedSearch = useRef(
    _.debounce((value) => {
      dispatch(searchCard(value));
    }, 300)
  ).current;

  const handleSearchChange = useCallback(
    (event) => {
      const value = event?.target?.value?.trimStart();
      setInputValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleSearchCancel = useCallback(() => {
    setInputValue("");
    setOpenSearchField(false);
    dispatch(searchCard(""));
  }, [dispatch]);

  const toggleDrawer = useCallback(
    (anchor, open) => (event) => {
      if (
        event &&
        event.type === "keydown" &&
        (event.key === "Tab" || event.key === "Shift")
      ) {
        return;
      }
      setAttachmentDialog((prev) => ({ ...prev, [anchor]: open }));
    },
    []
  );

  const removeSearchParams = useCallback(
    (paramsToRemove) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      paramsToRemove?.forEach((param) => {
        nextSearchParams.delete(param);
      });
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(
    (mode) => {
      removeSearchParams(mode === "clear" ? ["search"] : ["search", "milestone"]);
      dispatch(filterUser(null));
      dispatch(searchCard([]));
    },
    [dispatch, removeSearchParams]
  );

  const handleOpenSearchField = useCallback(() => {
    setOpenSearchField((previousValue) => !previousValue);
  }, []);

  const handleCloseFilterDrawer = useCallback(() => {
    setFilterDrawer(false);
  }, []);

  const handleOpenChangeBoard = useCallback((event) => {
    setChangeBoardAnchorEl(event.currentTarget);
  }, []);

  const handleCloseChangeBoard = useCallback(() => {
    setChangeBoardAnchorEl(null);
  }, []);

  const filteredParams = useMemo(() => {
    const keysToExtract = ["noMilestone", "milestone", "GantChart", "search"];
    const extracted = new URLSearchParams();

    keysToExtract.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) {
        extracted.set(key, value);
      }
    });

    return extracted.toString();
  }, [searchParams]);

  const handleWorklogsNavigate = useCallback(() => {
    navigate(
      `/board/${boardKey}?worklogs=true${
        filteredParams ? `&${filteredParams}` : ""
      }`
    );
    localStorageInstance.setItem({
      key: "showGridView",
      value: false,
    });
  }, [boardKey, filteredParams, navigate]);

  const handleFeedsNavigate = useCallback(() => {
    navigate(
      `/feeds/${boardKey}?worklogs=false${
        filteredParams ? `&${filteredParams}` : ""
      }`
    );
    localStorageInstance.setItem({
      key: "showGridView",
      value: false,
    });
  }, [boardKey, filteredParams, navigate]);

  return {
    attachmentDialog,
    boardList,
    cachedBoard,
    changeBoardAnchorEl,
    canAccessBoardHeaderMenu,
    clearFilters,
    filterDrawer,
    handleCloseChangeBoard,
    handleCloseFilterDrawer,
    handleFeedsNavigate,
    handleOpenChangeBoard,
    handleOpenSearchField,
    handleSearchCancel,
    handleSearchChange,
    handleWorklogsNavigate,
    inputValue,
    isMobile,
    isPermission,
    location,
    milestone,
    openChangeBoard: Boolean(changeBoardAnchorEl),
    openSearchField,
    paramsWorklogs,
    searchParams,
    selectedUsers,
    setAttachmentDialog,
    setFilterDrawer,
    setParamsWorklogs,
    setSearchParams,
    setShowSummary,
    showGridView,
    showSummary,
    toggleDrawer,
  };
}
